import { isSuccess } from '@devexperts/remote-data-ts'
import { Color, isSpotSubType, SpotType } from '@spiaggeit/spit-core'

import { License } from '@/models/license'
import {
  MapElementWithSectorId,
  MapSetupItemKeyName,
  WarehouseSetup,
} from '@/models/map'
import { QuoteReservation } from '@/models/quote'
import { cartSlice } from '@/store/cartSlice'
import type { RootState } from '@/store/configureStore'
import { licenseSlice } from '@/store/licenseSlice'
import { mapSelectors, mapSlice } from '@/store/mapSlice'
import { spotTypesWithoutSetupSeats } from '@/utils/constants'
import { getSeatDefaultQuantity } from '@/utils/getSeatDefaultQuantity'

import {
  getSectorOnlineSetupItems,
  getVisibleStagingSetupItems,
} from './getSectorOnlineSetupItems'

type WarehouseSetupBySectorId = Record<number, WarehouseSetup>

const seatKeyToWarehouseField: Record<
  | MapSetupItemKeyName.BED
  | MapSetupItemKeyName.CHAIR
  | MapSetupItemKeyName.DECK_CHAIR
  | MapSetupItemKeyName.MAXI_BED,
  keyof WarehouseSetup
> = {
  [MapSetupItemKeyName.BED]: 'beds',
  [MapSetupItemKeyName.CHAIR]: 'chairs',
  [MapSetupItemKeyName.DECK_CHAIR]: 'deckChairs',
  [MapSetupItemKeyName.MAXI_BED]: 'maxiBeds',
}

function isSeatSetupKey(
  keyName: MapSetupItemKeyName
): keyName is keyof typeof seatKeyToWarehouseField {
  return keyName in seatKeyToWarehouseField
}

function reservationMatchesSpot(
  reservation: QuoteReservation,
  spot: Pick<MapElementWithSectorId, 'name' | 'subType'>
) {
  return (
    reservation.spotName === spot.name && reservation.spotType === spot.subType
  )
}

function getDefaultSeatRequirements(
  visibleSetupItems: ReturnType<typeof getVisibleStagingSetupItems>
): WarehouseSetup {
  const requirements: WarehouseSetup = {
    beds: 0,
    chairs: 0,
    deckChairs: 0,
    maxiBeds: 0,
  }

  visibleSetupItems.forEach((setupItem) => {
    if (!isSeatSetupKey(setupItem.keyName)) {
      return
    }

    const quantity = getSeatDefaultQuantity({ setupItem })
    const field = seatKeyToWarehouseField[setupItem.keyName]
    requirements[field] = quantity
  })

  return requirements
}

function getRemainingWarehouseForSpotBooking(
  sectorWarehouse: WarehouseSetup,
  sectorId: number,
  cartReservations: QuoteReservation[],
  spot: Pick<MapElementWithSectorId, 'name' | 'subType'>,
  requiredSeats: WarehouseSetup
): WarehouseSetup {
  const availability = { ...sectorWarehouse }

  cartReservations.forEach((reservation) => {
    if (reservation.sectorId !== sectorId) {
      return
    }

    if (reservationMatchesSpot(reservation, spot)) {
      return
    }

    availability.beds -= reservation.beds
    availability.chairs -= reservation.chairs
    availability.deckChairs -= reservation.deckChairs
    availability.maxiBeds -= reservation.maxiBeds
  })

  availability.beds -= requiredSeats.beds
  availability.chairs -= requiredSeats.chairs
  availability.deckChairs -= requiredSeats.deckChairs
  availability.maxiBeds -= requiredSeats.maxiBeds

  return availability
}

export function isSpotBlockedByWarehouse(params: {
  license: License | null
  warehouseBySector: WarehouseSetupBySectorId | undefined
  sectorSetupItems: ReturnType<typeof getSectorOnlineSetupItems>
  spot: Pick<MapElementWithSectorId, 'name' | 'subType' | 'sectorId'>
  cartReservations: QuoteReservation[]
}): boolean {
  const {
    license,
    warehouseBySector,
    sectorSetupItems,
    spot,
    cartReservations,
  } = params

  if (!license?.warehouseOnline || !warehouseBySector || !spot.sectorId) {
    return false
  }

  if (spotTypesWithoutSetupSeats.includes(spot.subType as SpotType)) {
    return false
  }

  if (
    cartReservations.some((reservation) =>
      reservationMatchesSpot(reservation, spot)
    )
  ) {
    return false
  }

  const sectorWarehouse = warehouseBySector[spot.sectorId]
  if (!sectorWarehouse) {
    return false
  }

  const visibleSetupItems = getVisibleStagingSetupItems(sectorSetupItems)
  const requiredSeats = getDefaultSeatRequirements(visibleSetupItems)
  const remaining = getRemainingWarehouseForSpotBooking(
    sectorWarehouse,
    spot.sectorId,
    cartReservations,
    spot,
    requiredSeats
  )

  return visibleSetupItems.some((setupItem) => {
    if (!isSeatSetupKey(setupItem.keyName)) {
      return false
    }

    const warehouseField = seatKeyToWarehouseField[setupItem.keyName]
    const requestedSeats = requiredSeats[warehouseField]

    if (requestedSeats <= 0) {
      return false
    }

    return remaining[warehouseField] < 0
  })
}

export function applyWarehouseMapAvailability(
  elements: MapElementWithSectorId[],
  options: {
    license: License | null
    warehouseBySector: WarehouseSetupBySectorId | undefined
    sectorsById: Map<
      number,
      { setupItems: ReturnType<typeof getSectorOnlineSetupItems> }
    >
    cartReservations: QuoteReservation[]
  }
): MapElementWithSectorId[] {
  const { license, warehouseBySector, sectorsById, cartReservations } = options

  if (!license?.warehouseOnline || !warehouseBySector) {
    return elements
  }

  return elements.map((element) => {
    if (!isSpotSubType(element.subType)) {
      return element
    }

    const apiAvailable =
      element.apiAvailable ?? element.style?.iconColor === Color.GREEN

    if (!apiAvailable) {
      return element
    }

    const sector = element.sectorId
      ? sectorsById.get(element.sectorId)
      : undefined

    if (
      isSpotBlockedByWarehouse({
        cartReservations,
        license,
        sectorSetupItems: sector?.setupItems ?? [],
        spot: element,
        warehouseBySector,
      })
    ) {
      return {
        ...element,
        style: {
          ...element.style,
          iconColor: Color.GREY,
        },
      }
    }

    return {
      ...element,
      style: {
        ...element.style,
        iconColor: Color.GREEN,
      },
    }
  })
}

export function getAvailableMapSpots(
  elements: MapElementWithSectorId[]
): MapElementWithSectorId[] {
  return elements.filter((item) => {
    if (!isSpotSubType(item.subType)) {
      return false
    }

    return item.style?.iconColor === Color.GREEN
  })
}

export function computeWarehouseAdjustedMapElements(
  state: RootState
): MapElementWithSectorId[] | null {
  const mapData = mapSelectors.mapRemoteData(state)
  const warehouse = mapSlice.selectors.warehouse(state)

  if (!isSuccess(mapData) || !mapData.value.elements) {
    return null
  }

  if (!isSuccess(warehouse)) {
    return mapData.value.elements
  }

  const sectorsById = new Map(
    mapSelectors
      .sectors(state)
      .map((sector) => [
        sector.header.id,
        { setupItems: getSectorOnlineSetupItems(sector) },
      ])
  )

  return applyWarehouseMapAvailability(mapData.value.elements, {
    cartReservations: cartSlice.selectors.cartReservations(state),
    license: licenseSlice.selectors.license(state),
    sectorsById,
    warehouseBySector: warehouse.value,
  })
}
